import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Save, Heart } from "lucide-react";
import {
  upsertCarePreferencesSchema,
  type UpsertCarePreferencesInput,
} from "@shared/validation/care.schema";
import { NOTES_MAX } from "@shared/constants/validation";
import {
  useCarePreferences,
  useSaveCarePreferences,
} from "../../lib/api/useCarePreferences";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS } from "../../lib/surfaces";

interface CarePreferencesSectionProps {
  childId: string;
}

interface FormShape {
  behavioralNotes: string;
  calmingStrategies: string;
  comfortItems: string;
  communicationStyle: string;
}

function nullToEmpty(value: string | null): string {
  return value ?? "";
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function CarePreferencesSection({ childId }: CarePreferencesSectionProps) {
  const { data: prefs, isLoading, error } = useCarePreferences(childId);
  const saveMutation = useSaveCarePreferences(childId);
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormShape>({
    resolver: zodResolver(upsertCarePreferencesSchema),
    defaultValues: {
      behavioralNotes: "",
      calmingStrategies: "",
      comfortItems: "",
      communicationStyle: "",
    },
  });

  useEffect(() => {
    if (prefs) {
      reset({
        behavioralNotes: nullToEmpty(prefs.behavioralNotes),
        calmingStrategies: nullToEmpty(prefs.calmingStrategies),
        comfortItems: nullToEmpty(prefs.comfortItems),
        communicationStyle: nullToEmpty(prefs.communicationStyle),
      });
    }
  }, [prefs, reset]);

  const onSubmit = async (data: FormShape) => {
    const payload: UpsertCarePreferencesInput = {
      behavioralNotes: emptyToNull(data.behavioralNotes),
      calmingStrategies: emptyToNull(data.calmingStrategies),
      comfortItems: emptyToNull(data.comfortItems),
      communicationStyle: emptyToNull(data.communicationStyle),
    };
    try {
      await saveMutation.mutateAsync(payload);
      publish({
        variant: "success",
        title: "Care preferences saved successfully",
      });
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to save care preferences",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <SectionCard
      icon={Heart}
      title="Care Preferences & Behavioral Notes"
      accent={SECTION_ACCENT.care}
    >
      {isLoading ? (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading care preferences…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">
          Failed to load care preferences: {error.message}
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-600">
            Help caregivers soothe your child the way you would.
          </p>

          {/* Two columns of fields on a wide profile page; one on a phone. */}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <CareField
              label="Behavioral Notes"
              id="care-behavioral"
              placeholder="Any behavioral patterns, triggers, or special considerations caregivers should know about?"
              error={errors.behavioralNotes?.message}
              value={watch("behavioralNotes") ?? ""}
              register={register("behavioralNotes")}
            />
            <CareField
              label="Calming Strategies"
              id="care-calming"
              placeholder="What Soothes Your Child — techniques, routines, or phrases that help them calm down."
              error={errors.calmingStrategies?.message}
              value={watch("calmingStrategies") ?? ""}
              register={register("calmingStrategies")}
            />
            <CareField
              label="Comfort Items"
              id="care-comfort"
              placeholder="What comfort items does your child have? (e.g., stuffed animals, blankets, noise-canceling headphones)"
              error={errors.comfortItems?.message}
              value={watch("comfortItems") ?? ""}
              register={register("comfortItems")}
            />
            <CareField
              label="Communication Style"
              id="care-communication"
              placeholder="How does your child communicate best? Verbal, nonverbal, assisted devices, preferred languages…"
              error={errors.communicationStyle?.message}
              value={watch("communicationStyle") ?? ""}
              register={register("communicationStyle")}
            />

            <div className="flex items-center justify-end lg:col-span-2">
              <Button
                type="submit"
                disabled={!isDirty}
                isLoading={isSubmitting || saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  "Saving…"
                ) : (
                  <>
                    <Save className="h-4 w-4" aria-hidden />
                    Save Preferences
                  </>
                )}
              </Button>
            </div>
          </form>
        </>
      )}
    </SectionCard>
  );
}

interface CareFieldProps {
  label: string;
  id: string;
  placeholder: string;
  error?: string;
  value: string;
  register: ReturnType<ReturnType<typeof useForm<FormShape>>["register"]>;
}

function CareField({ label, id, placeholder, error, value, register }: CareFieldProps) {
  const length = value.length;
  const overLimit = length > NOTES_MAX;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="text-sm font-medium text-neutral-800">
          {label}
        </label>
        <span
          className={`text-xs tabular-nums ${overLimit ? "text-red-600" : "text-neutral-600"}`}
        >
          {length} / {NOTES_MAX}
        </span>
      </div>
      <textarea
        id={id}
        rows={3}
        placeholder={placeholder}
        {...register}
        className={INPUT_CLASS}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
